<script>
import {
  iAccordion,
  iAccordionItem,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iSpinner,
} from '@inscribe/signal';
import {
  displayMetadataValue,
  flattenMetadata,
  sortMetadata,
} from '@/utils/metadata';
import InfoIcon from '@/V2/components/InfoIcon';
import SignalMetadataLine from '@/V2/documents/document-view/signals/SignalMetadataLine.vue';

export default {
  components: {
    iAccordion,
    iAccordionItem,
    InfoIcon,
    SignalMetadataLine,
    iSpinner,
    iButton,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
  },
  data() {
    return { iButtonSizes, iButtonStyles, iButtonTypes, showFullList: false };
  },
  computed: {
    documentFontsMetadata() {
      return this.$store.state.documentFontsMetadata;
    },
    flattenedMetadata() {
      if (!this.document?.metadata) {
        return {};
      }
      const flattenedMetadata = {};
      Object.keys(this.document.metadata).forEach((key) => {
        Object.keys(this.document.metadata[key]).forEach((subKey) => {
          if (!flattenedMetadata[key]) {
            flattenedMetadata[key] = {};
          }
          let lowerSubKey = subKey;
          if (subKey[1] !== subKey[1].toUpperCase()) {
            lowerSubKey =
              subKey[0].toLowerCase() +
              subKey[1].toLowerCase() +
              subKey.slice(2);
          }
          flattenedMetadata[key][lowerSubKey] =
            this.document.metadata[key][subKey];
        });
      });
      return flattenMetadata(flattenedMetadata);
    },
    sortedMetadata() {
      const metadata = sortMetadata(
        Object.entries(this.flattenedMetadata).map(([label, value]) => ({
          label,
          value,
        })),
      );
      return this.showFullList ? metadata : metadata.slice(0, 8);
    },
  },
  mounted() {
    this.getDocumentFontsMetadata();
  },
  methods: {
    getDocumentFontsMetadata() {
      this.$store.commit('setFontsMetadataLoading', true);
      this.$store
        .dispatch('getFontsMetadata', {
          customerId: this.document.customer_id,
          documentId: this.document.id,
        })
        .then(() => {
          this.$store.commit('setFontsMetadataLoading', false);
        });
    },
    getMetadataValue(metadataArray) {
      if (!metadataArray?.length) return '';
      const allSameValue = metadataArray.every(
        (item) => item.value === metadataArray[0].value,
      );
      if (allSameValue) {
        return displayMetadataValue(metadataArray[0].value);
      }
      return metadataArray
        .map((item) => `${displayMetadataValue(item.value)} (${item.type})`)
        .join('<br/>');
    },
  },
};
</script>

<template>
  <div class="document-details-panel d-flex flex-column">
    <!-- document metadata -->
    <i-accordion :allow-multi-open="true" data-testid="metadata-list">
      <!-- doc info -->
      <i-accordion-item
        title="Metadata"
        :no-background="true"
        :mount-open="true"
        size="large"
      >
        <template #extra-title-content>
          <info-icon
            class="i-mr-4"
            title="Document Metadata"
            description="Metadata about the document, including the document type, date, and other relevant information."
          />
        </template>
        <div
          class="document-stats d-flex flex-column align-items-start justify-content-start accordion-content"
        >
          <signal-metadata-line
            v-for="metadata in sortedMetadata"
            :title="metadata.label"
          >
            <template #value>
              <span v-html="getMetadataValue(metadata.value)"></span>
            </template>
          </signal-metadata-line>

          <!-- show more button -->
          <i-button
            v-if="sortedMetadata.length > 8"
            class="show-more-button i-mt-6"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.SOLID_LIGHT"
            :size="iButtonSizes.SMALL"
            :label="showFullList ? 'Show Less' : 'Show More'"
            @button-click="showFullList = !showFullList"
            >Show More</i-button
          >
        </div>
      </i-accordion-item>

      <!-- fonts -->
      <i-accordion-item
        :no-background="true"
        :mount-open="true"
        title="Document Fonts"
        size="large"
      >
        <template #extra-title-content>
          <info-icon
            class="i-mr-4"
            title="Document Fonts"
            description="List of fonts used in the document."
          />
        </template>

        <div
          class="document-stats d-flex flex-column align-items-start justify-content-start accordion-content"
        >
          <i-spinner v-if="documentFontsMetadata.isLoading"></i-spinner>
          <div
            v-else-if="documentFontsMetadata.fonts.length === 0"
            class="i-text-neutral-120 i-text-subheading-3"
          >
            No fonts were found in this document
          </div>
          <div v-else class="fonts-list">
            <signal-metadata-line
              v-for="font in documentFontsMetadata.fonts"
              :key="font.name"
              :title="font.name"
            >
            </signal-metadata-line>
          </div>
        </div>
      </i-accordion-item>
    </i-accordion>
  </div>
</template>

<style lang="scss">
.document-details-panel {
  width: 100%;
  overflow-y: auto;

  .fonts-list {
    width: 100%;
  }

  .show-more-button {
    width: 100%;
  }
}
</style>
